import React, { useEffect, useState } from 'react';
import { previoDe, mensajeDeError } from '../api';

/** Texto seguro dentro del HTML que se imprime. Mismo criterio que en el sistema interno. */
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const AFORO = {
  ROJO:     { texto: 'Rojo',     color: '#b91c1c', fondo: '#fef2f2', borde: '#fca5a5' },
  AMARILLO: { texto: 'Amarillo', color: '#a16207', fondo: '#fefce8', borde: '#fde047' },
  VERDE:    { texto: 'Verde',    color: '#15803d', fondo: '#f0fdf4', borde: '#86efac' },
};

const TECHO = { DURO: 'Duro', CONVERTIBLE: 'Convertible', PANORAMICO: 'Panorámico' };

const estiloCampo = {
  display: 'flex', justifyContent: 'space-between', gap: 12,
  padding: '6px 0', borderBottom: '1px solid var(--gris-borde)', fontSize: 13,
};

/** Una línea "etiqueta … valor", como en el modal del sistema interno. */
const Campo = ({ etiqueta, children }) => (
  <div style={estiloCampo}>
    <span style={{ color: 'var(--gris-texto)' }}>{etiqueta}</span>
    <strong style={{ textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>{children}</strong>
  </div>
);

const Seccion = ({ children }) => (
  <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--azul-oscuro)', margin: '14px 0 4px' }}>
    {children}
  </div>
);

/**
 * El previo de un trámite, como lo ve el cliente.
 *
 * Se ve igual que el modal del sistema interno —mismas secciones y mismo orden— con dos
 * diferencias, las dos pedidas por Axel:
 *
 *  - NINGUNA fecha ni hora. Ni la cita, ni cuándo empezó o terminó. El cliente ve QUÉ pasó con
 *    su mercadería, no cuánto tardó WF.
 *  - No sale nada del trabajo interno: ni el consecutivo del trámite ni quién lo creó. Sí el
 *    responsable, que es a quien preguntarle.
 *
 * La impresión la dispara ESTA ventana sobre la que se abre; el reporte no lleva <script> propio.
 * Eso permite que la política de seguridad del portal prohíba los scripts en línea sin romper la
 * impresión.
 */
export default function ModalPrevio({ previoId, documentoAsociado, alCerrar }) {
  const [previo, setPrevio] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    previoDe(previoId)
      .then(({ data }) => { if (vivo) setPrevio(data); })
      .catch((err) => { if (vivo) setError(mensajeDeError(err, 'No se pudo cargar el previo.')); });
    return () => { vivo = false; };
  }, [previoId]);

  useEffect(() => {
    const alTeclear = (e) => { if (e.key === 'Escape') alCerrar(); };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [alCerrar]);

  const imprimir = () => {
    if (!previo) return;
    const fila = (etiqueta, valor) => (valor === null || valor === undefined || valor === ''
      ? '' : `<tr><td>${esc(etiqueta)}</td><td>${esc(valor)}</td></tr>`);
    const v = previo.vehiculo;

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"/>
<title>${esc(previo.tipo)} — ${esc(documentoAsociado)}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; color: #1e293b; padding: 24px; }
  h1 { font-size: 17px; color: #0f2b46; margin: 0 0 2px; }
  .sub { color: #64748b; font-size: 12px; margin-bottom: 14px; }
  .seccion { font-size: 12px; font-weight: 700; color: #0f2b46; margin: 14px 0 4px; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  td:first-child { width: 36%; color: #64748b; }
  .obs { font-style: italic; color: #475569; margin-top: 6px; }
  .dano { margin-top: 10px; padding: 8px 10px; background: #fef2f2; border: 1px solid #fca5a5; }
  .fotos { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
  .fotos img { width: 31%; border: 1px solid #e2e8f0; border-radius: 6px; }
  @page { size: A4; margin: 1.5cm; }
</style></head><body>
<h1>${esc(previo.tipo)}</h1>
<div class="sub">Trámite ${esc(documentoAsociado)} · ${esc(previo.estado)}</div>

<div class="seccion">Datos del trámite</div>
<table>
  ${fila('N° DUA', previo.numDua)}
  ${fila('Aforo', previo.aforo && AFORO[previo.aforo] ? AFORO[previo.aforo].texto : null)}
</table>

<div class="seccion">Datos del operario</div>
<table>
  ${fila('Responsable del previo', previo.responsableNombre)}
  ${fila('Almacén fiscal', previo.almacenFiscal
    ? previo.almacenFiscal + (previo.almacenFiscalCodigo ? ` (${previo.almacenFiscalCodigo})` : '') : null)}
  ${fila('Consignatario', previo.consignatario)}
  ${fila('Movimiento', previo.movimiento)}
  ${fila('Guía/BL/Carta Porte', previo.guiaBlCartaPorte)}
  ${fila('Bultos', previo.bultos)}
  ${fila('Peso', previo.pesoKgs != null ? `${previo.pesoKgs} kg` : null)}
  ${fila('VIN', previo.vin)}
  ${fila('Número de cita', previo.numeroCita)}
</table>
${previo.observaciones ? `<div class="obs">Observaciones del operario: “${esc(previo.observaciones)}”</div>` : ''}

${(previo.tarimas && previo.tarimas.length > 0) ? `
<div class="seccion">Tarimas (transportista)</div>
<table>
  <tr><td>N° tarima</td><td>Peso (kg) · Bultos · Dimensiones</td></tr>
  ${previo.tarimas.map((t) => `<tr><td>${esc(t.numeroTarima || '—')}</td><td>${esc(t.pesoTarimaKg ?? '—')} · ${esc(t.bultos ?? '—')} · ${esc(t.dimensiones || '—')}</td></tr>`).join('')}
</table>` : ''}

${v ? `
<div class="seccion">Datos del vehículo (transportista)</div>
<table>
  ${fila('Kilometraje', v.kilometraje != null ? `${v.kilometraje} ${v.unidadKilometraje || ''}`.trim() : null)}
  ${fila('Marca / Modelo / Año', [v.marca, v.modelo, v.anio].filter(Boolean).join(' '))}
  ${fila('Combustible / Transmisión', [v.combustible, v.transmision].filter(Boolean).join(' · '))}
  ${fila('Color / Puertas / Pasajeros', [v.colorCarro, v.numeroPuertas, v.numeroPasajeros].filter((x) => x != null && x !== '').join(' · '))}
  ${fila('4x4', v.es4x4 == null ? null : (v.es4x4 ? 'Sí' : 'No'))}
</table>` : ''}

${previo.observacionesTransportista
    ? `<div class="seccion">Observaciones del transportista</div><div class="obs">“${esc(previo.observacionesTransportista)}”</div>` : ''}
${previo.danoMercaderia
    ? `<div class="dano"><strong>Daño reportado en la mercadería</strong><br/>${esc(previo.danoMercaderiaDescripcion || '')}</div>` : ''}

${(previo.fotos && previo.fotos.length > 0)
    ? `<div class="seccion">Fotos de evidencia (${previo.fotos.length})</div><div class="fotos">${previo.fotos.map((f) => `<img src="${esc(f.url)}" alt="" />`).join('')}</div>` : ''}
</body></html>`;

    const ventana = window.open('', '_blank', 'width=900,height=700');
    if (!ventana) {
      setError('El navegador bloqueó la ventana. Permita las ventanas emergentes para imprimir.');
      return;
    }
    ventana.document.write(html);
    ventana.document.close();
    let impreso = false;
    const mandarAImprimir = () => {
      if (impreso) return;
      impreso = true;
      ventana.onafterprint = () => ventana.close();
      ventana.focus();
      ventana.print();
    };
    // Se espera a que carguen las fotos: si no, salen en blanco.
    if (ventana.document.readyState === 'complete') setTimeout(mandarAImprimir, 0);
    else ventana.addEventListener('load', mandarAImprimir, { once: true });
  };

  const v = previo && previo.vehiculo;
  const aforo = previo && previo.aforo ? AFORO[previo.aforo] : null;

  return (
    <div
      onClick={alCerrar}
      style={{
        position: 'fixed', inset: 0, zIndex: 110, padding: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.55)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="tarjeta"
        style={{
          width: '100%', maxWidth: 620, maxHeight: '88vh',
          display: 'flex', flexDirection: 'column', boxShadow: 'var(--sombra-md)',
        }}
      >
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--gris-borde)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 800 }}>
              📋 {previo ? previo.tipo : 'Previo'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--gris-texto)', marginTop: 2 }}>
              {documentoAsociado}
            </div>
          </div>
          {previo && (
            <span style={{
              fontSize: 11, fontWeight: 800, borderRadius: 20, padding: '3px 10px',
              color: 'var(--sobre-tinte-verde)', background: 'var(--verde-claro)',
              border: '1px solid var(--sobre-tinte-verde)', whiteSpace: 'nowrap',
            }}>
              {previo.estado}
            </span>
          )}
        </div>

        <div style={{ padding: '10px 20px 18px', overflowY: 'auto', flex: 1 }}>
          {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}

          {!previo && !error && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--gris-texto)' }}>Cargando…</div>
          )}

          {previo && (
            <>
              {(previo.numDua || aforo) && (
                <>
                  <Seccion>Datos del trámite</Seccion>
                  {previo.numDua && <Campo etiqueta="🧾 N° DUA">{previo.numDua}</Campo>}
                  {aforo && (
                    <Campo etiqueta="🚦 Aforo">
                      <span style={{
                        color: aforo.color, background: aforo.fondo, border: `1px solid ${aforo.borde}`,
                        borderRadius: 20, padding: '1px 9px',
                      }}>{aforo.texto}</span>
                    </Campo>
                  )}
                </>
              )}

              <Seccion>Datos del operario</Seccion>
              <Campo etiqueta="🧑‍💼 Responsable del previo">
                {previo.responsableNombre || 'Sin responsable asignado'}
              </Campo>
              {previo.almacenFiscal && (
                <Campo etiqueta="🏬 Almacén fiscal">
                  {previo.almacenFiscal}{previo.almacenFiscalCodigo ? ` (${previo.almacenFiscalCodigo})` : ''}
                </Campo>
              )}
              {previo.consignatario && <Campo etiqueta="👤 Consignatario">{previo.consignatario}</Campo>}
              {previo.movimiento && <Campo etiqueta="📦 Movimiento">{previo.movimiento}</Campo>}
              {previo.guiaBlCartaPorte && <Campo etiqueta="📄 Guía/BL/Carta Porte">{previo.guiaBlCartaPorte}</Campo>}
              {previo.bultos != null && <Campo etiqueta="📦 Bultos">{previo.bultos}</Campo>}
              {previo.pesoKgs != null && <Campo etiqueta="⚖️ Peso">{previo.pesoKgs} kg</Campo>}
              {previo.vin && <Campo etiqueta="🚗 VIN">{previo.vin}</Campo>}
              {previo.subtipoVehiculo && (
                <Campo etiqueta="🔧 Tipo">{previo.subtipoVehiculo === 'DEKRA' ? 'DEKRA' : 'Revisión'}</Campo>
              )}
              {previo.numeroCita && <Campo etiqueta="🎫 Número de cita">{previo.numeroCita}</Campo>}
              {previo.transportistaNombre && (
                <Campo etiqueta="🚚 Transportista">{previo.transportistaNombre}</Campo>
              )}
              {previo.observaciones && (
                <div style={{ fontSize: 12.5, color: 'var(--gris-texto)', marginTop: 7, fontStyle: 'italic' }}>
                  Observaciones del operario: “{previo.observaciones}”
                </div>
              )}

              {previo.tipo === 'Previo de examen' && (
                <>
                  <Seccion>Tarimas (transportista)</Seccion>
                  {previo.tarimas && previo.tarimas.length > 0 ? (
                    <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', color: 'var(--gris-texto)' }}>
                          <th style={{ padding: '4px 6px 4px 0', fontWeight: 600 }}>N° tarima</th>
                          <th style={{ padding: '4px 6px', fontWeight: 600 }}>Peso (kg)</th>
                          <th style={{ padding: '4px 6px', fontWeight: 600 }}>Bultos</th>
                          <th style={{ padding: '4px 6px', fontWeight: 600 }}>Dimensiones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previo.tarimas.map((t) => (
                          <tr key={t.id} style={{ borderTop: '1px solid var(--gris-borde)' }}>
                            <td style={{ padding: '4px 6px 4px 0' }}>{t.numeroTarima || '—'}</td>
                            <td style={{ padding: '4px 6px' }}>{t.pesoTarimaKg ?? '—'}</td>
                            <td style={{ padding: '4px 6px' }}>{t.bultos ?? '—'}</td>
                            <td style={{ padding: '4px 6px' }}>{t.dimensiones || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12.5, color: 'var(--gris-texto)' }}>Sin tarimas registradas todavía.</div>
                  )}
                </>
              )}

              {previo.tipo === 'Previo de vehículo' && (
                <>
                  <Seccion>Datos del vehículo (transportista)</Seccion>
                  {v ? (
                    <>
                      <Campo etiqueta="Kilometraje">{v.kilometraje ?? '—'} {v.unidadKilometraje || ''}</Campo>
                      <Campo etiqueta="Marca / Modelo / Año">{v.marca || '—'} {v.modelo || ''} {v.anio || ''}</Campo>
                      <Campo etiqueta="Combustible / Transmisión">
                        {v.combustible || '—'} · {v.transmision || '—'}
                        {v.numeroMarchas ? ` (${v.numeroMarchas} marchas)` : ''}
                      </Campo>
                      <Campo etiqueta="Asientos / Pasajeros">{v.tipoAsiento || '—'} · {v.numeroPasajeros ?? '—'} pax</Campo>
                      <Campo etiqueta="Color / Techo / Puertas">
                        {v.colorCarro || '—'} · {TECHO[v.techo] || v.techo || '—'} · {v.numeroPuertas ?? '—'}
                      </Campo>
                      <Campo etiqueta="4x4">{v.es4x4 == null ? '—' : (v.es4x4 ? 'Sí' : 'No')}</Campo>
                    </>
                  ) : (
                    <div style={{ fontSize: 12.5, color: 'var(--gris-texto)' }}>
                      El transportista todavía no lo ha completado.
                    </div>
                  )}
                </>
              )}

              {previo.observacionesTransportista && (
                <>
                  <Seccion>Observaciones del transportista</Seccion>
                  <div style={{ fontSize: 12.5, color: 'var(--gris-texto)', fontStyle: 'italic' }}>
                    “{previo.observacionesTransportista}”
                  </div>
                </>
              )}

              {previo.danoMercaderia && (
                <div style={{
                  marginTop: 12, padding: '8px 10px', borderRadius: 8,
                  background: 'var(--tinte-rojo)', border: '1px solid var(--sobre-tinte-rojo)',
                }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--sobre-tinte-rojo)', marginBottom: 4 }}>
                    ⚠️ Daño reportado en la mercadería
                  </div>
                  <div style={{ fontSize: 12.5 }}>{previo.danoMercaderiaDescripcion}</div>
                </div>
              )}

              <Seccion>Fotos de evidencia {previo.fotos ? `(${previo.fotos.length})` : '(0)'}</Seccion>
              {previo.fotos && previo.fotos.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {previo.fotos.map((f) => (
                    <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" title="Abrir la foto">
                      <img
                        src={f.url} alt="Evidencia del previo"
                        style={{
                          width: 84, height: 84, objectFit: 'cover', borderRadius: 8,
                          border: '1px solid var(--gris-borde)', display: 'block',
                        }}
                      />
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--gris-texto)' }}>Sin fotos subidas todavía.</div>
              )}
            </>
          )}
        </div>

        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--gris-borde)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button type="button" className="btn" onClick={imprimir} disabled={!previo}>
            🖨️ Imprimir o guardar PDF
          </button>
          <button type="button" className="btn" onClick={alCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
